/* osr-cart-qty-guard.js — Cart qty enforcement for correction products
   Rules:
   - oeil_gauche !== oeil_droit  → min qty 2, only even steps (2,4,6...)
   - Same correction OR no correction → normal behavior, no restrictions
*/
(function () {
  'use strict';

  const normalize = function (v) { return (v || '').toString().trim(); };

  /* ── Read correction properties from a cart row's DOM ── */
  function getCorrectionProps(rowEl) {
    /* Dawn renders item.properties as <dl> with <dt>key</dt><dd>value</dd> pairs */
    const dts = rowEl.querySelectorAll('.product-option dt');
    let og = '', od = '';
    dts.forEach(function (dt) {
      const key  = normalize(dt.textContent).replace(/:$/, '').toLowerCase();
      const val  = normalize(dt.nextElementSibling && dt.nextElementSibling.textContent);
      if (key === 'oeil_gauche') og = val;
      if (key === 'oeil_droit')  od = val;
    });
    return { og: og, od: od };
  }

  function isDiffCorrection(rowEl) {
    const p = getCorrectionProps(rowEl);
    return p.og && p.od && p.og !== p.od;
  }

  /* ── Apply visual guard to a single row ── */
  function guardRow(rowEl) {
    const qtyInput = rowEl.querySelector('input.quantity__input, input[name="updates[]"]');
    if (!qtyInput) return;

    const minusBtn = rowEl.querySelector('button[name="minus"], .quantity__button[name="minus"]');
    const plusBtn  = rowEl.querySelector('button[name="plus"],  .quantity__button[name="plus"]');

    if (!isDiffCorrection(rowEl)) {
      /* Normal product — restore everything */
      if (minusBtn) {
        minusBtn.disabled = false;
        minusBtn.classList.remove('osr-qty-disabled');
      }
      rowEl.removeAttribute('data-osr-min2');
      return;
    }

    /* Diff correction — enforce min 2, even steps */
    rowEl.setAttribute('data-osr-min2', 'true');

    let current = parseInt(qtyInput.value, 10) || 2;
    if (current < 2)           current = 2;
    if (current % 2 !== 0)    current = current + 1;

    /* Only force-correct if the stored value is wrong */
    if (parseInt(qtyInput.value, 10) !== current) {
      qtyInput.value = String(current);
    }

    /* Disable minus when at minimum */
    if (minusBtn) {
      const atMin = current <= 2;
      minusBtn.disabled = atMin;
      minusBtn.classList.toggle('osr-qty-disabled', atMin);
    }
  }

  /* ── Guard all rows in document ── */
  function guardAllRows() {
    /* Main cart: rows have id="CartItem-N" */
    /* Cart drawer: rows have id="CartDrawer-Item-N" */
    document.querySelectorAll(
      'tr.cart-item[id^="CartItem-"], tr.cart-item[id^="CartDrawer-Item-"]'
    ).forEach(guardRow);
  }

  /* ── Intercept + button clicks ── */
  document.addEventListener('click', function (e) {
    const plusBtn = e.target.closest('button[name="plus"]');
    if (!plusBtn) return;

    const rowEl = plusBtn.closest(
      'tr.cart-item[id^="CartItem-"], tr.cart-item[id^="CartDrawer-Item-"]'
    );
    if (!rowEl || !isDiffCorrection(rowEl)) return;

    const qtyInput = rowEl.querySelector('input.quantity__input, input[name="updates[]"]');
    if (!qtyInput) return;

    const current = parseInt(qtyInput.value, 10) || 2;
    const next    = current + 2; /* always jump by 2 */

    /* Let the click proceed only if we manually set the correct next value.
       We set it here; Dawn's quantity-input web component will fire the change. */
    e.preventDefault();
    e.stopImmediatePropagation();

    const nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
    nativeSetter.call(qtyInput, String(next));
    qtyInput.dispatchEvent(new Event('change', { bubbles: true }));

    /* Re-evaluate minus button state */
    guardRow(rowEl);
  }, true); /* capture phase — runs before Dawn's listener */

  /* ── Intercept minus button clicks ── */
  document.addEventListener('click', function (e) {
    const minusBtn = e.target.closest('button[name="minus"]');
    if (!minusBtn) return;

    const rowEl = minusBtn.closest(
      'tr.cart-item[id^="CartItem-"], tr.cart-item[id^="CartDrawer-Item-"]'
    );
    if (!rowEl || !isDiffCorrection(rowEl)) return;

    const qtyInput = rowEl.querySelector('input.quantity__input, input[name="updates[]"]');
    if (!qtyInput) return;

    const current = parseInt(qtyInput.value, 10) || 2;

    /* Block going below 2 */
    if (current <= 2) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    /* Allow going down by 2 steps only */
    e.preventDefault();
    e.stopImmediatePropagation();

    const next = current - 2;
    const nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
    nativeSetter.call(qtyInput, String(next));
    qtyInput.dispatchEvent(new Event('change', { bubbles: true }));

    guardRow(rowEl);
  }, true);

  /* ── Network-level guard: block quantity < 2 via fetch ── */
  (function hardenFetch() {
    if (!window.fetch) return;
    const origFetch = window.fetch.bind(window);

    window.fetch = async function (input, init) {
      try {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        const isCartChange = /\/cart\/(change|update)\.js/.test(url);

        if (isCartChange && init && init.body) {
          let body = null;
          try { body = typeof init.body === 'string' ? JSON.parse(init.body) : null; } catch (e) {}

          if (body) {
            /* /cart/change.js: { line, quantity } */
            if (typeof body.quantity !== 'undefined' && typeof body.line !== 'undefined') {
              /* Find the cart row for this line index */
              const lineIndex = parseInt(body.line, 10);
              const rowEl = document.getElementById('CartItem-' + lineIndex)
                         || document.getElementById('CartDrawer-Item-' + lineIndex);

              if (rowEl && isDiffCorrection(rowEl)) {
                let safeQty = parseInt(body.quantity, 10);
                if (safeQty > 0 && safeQty < 2)       safeQty = 2;
                if (safeQty > 0 && safeQty % 2 !== 0) safeQty = safeQty + 1;
                if (safeQty !== parseInt(body.quantity, 10)) {
                  body.quantity = safeQty;
                  init = Object.assign({}, init, { body: JSON.stringify(body) });
                }
              }
            }

            /* /cart/update.js: { updates: { key: qty, ... } } */
            if (body.updates && typeof body.updates === 'object') {
              let modified = false;
              Object.keys(body.updates).forEach(function (key) {
                const qty = parseInt(body.updates[key], 10);
                if (qty <= 0) return; /* removal — allow */

                /* Match key against DOM rows */
                const allRows = document.querySelectorAll(
                  'tr.cart-item[id^="CartItem-"], tr.cart-item[id^="CartDrawer-Item-"]'
                );
                allRows.forEach(function (rowEl) {
                  const input = rowEl.querySelector('[data-quantity-variant-id]');
                  if (!input) return;
                  /* Dawn uses line index or variant id as key */
                  if (isDiffCorrection(rowEl)) {
                    let safeQty = qty;
                    if (safeQty < 2)         safeQty = 2;
                    if (safeQty % 2 !== 0)   safeQty = safeQty + 1;
                    if (safeQty !== qty) {
                      body.updates[key] = safeQty;
                      modified = true;
                    }
                  }
                });
              });
              if (modified) {
                init = Object.assign({}, init, { body: JSON.stringify(body) });
              }
            }
          }
        }
      } catch (err) { /* never break the request */ }

      const response = await origFetch(input, init);

      /* After any cart mutation, re-guard all rows */
      try {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        if (/\/cart\/(change|add|update)\.js/.test(url)) {
          setTimeout(guardAllRows, 80);
          setTimeout(guardAllRows, 400);
        }
      } catch (err) {}

      return response;
    };
  })();

  /* ── Re-guard after Dawn re-renders cart sections ── */
  function setupObserver() {
    const targets = [
      document.getElementById('main-cart-items'),
      document.getElementById('CartDrawer-CartItems'),
      document.querySelector('cart-drawer-items'),
      document.querySelector('cart-items'),
    ].filter(Boolean);

    if (targets.length === 0) return;

    const mo = new MutationObserver(function () {
      setTimeout(guardAllRows, 50);
    });
    targets.forEach(function (el) {
      mo.observe(el, { childList: true, subtree: true });
    });
  }



  /* ── PubSub hook — Dawn's own cart update event ── */
  function setupPubSub() {
    if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        setTimeout(guardAllRows, 100);
        setTimeout(guardAllRows, 500);
      });
    }
  }

  /* ── Intercept keyboard input (typed qty) ── */
  document.addEventListener('input', function (e) {
    if (!e.target.matches('input.quantity__input, input[name^="updates"]')) return;

    const rowEl = e.target.closest(
      'tr.cart-item[id^="CartItem-"], tr.cart-item[id^="CartDrawer-Item-"]'
    );
    if (!rowEl || !isDiffCorrection(rowEl)) return;

    const qtyInput = e.target;
    let val = parseInt(qtyInput.value, 10);
    if (isNaN(val) || val < 2) val = 2;
    else if (val % 2 !== 0) val = val + 1;

    if (String(qtyInput.value) !== String(val)) {
      const nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
      nativeSetter.call(qtyInput, String(val));
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    guardRow(rowEl);
  });

  document.addEventListener('change', function (e) {
    if (!e.target.matches('input.quantity__input, input[name^="updates"]')) return;

    const rowEl = e.target.closest(
      'tr.cart-item[id^="CartItem-"], tr.cart-item[id^="CartDrawer-Item-"]'
    );
    if (!rowEl || !isDiffCorrection(rowEl)) return;

    const qtyInput = e.target;
    let val = parseInt(qtyInput.value, 10);
    if (isNaN(val) || val < 2) val = 2;
    else if (val % 2 !== 0) val = val + 1;

    if (String(qtyInput.value) !== String(val)) {
      const nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
      nativeSetter.call(qtyInput, String(val));
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    guardRow(rowEl);
  });
  /* ── Boot ── */
  function init() {
    guardAllRows();
    setupObserver();
    setupPubSub();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();